export const DEFAULT_PER_PAGE = 20;

export type TabId = "overview" | "enqueued" | "working" | "pending" | "failed";
export type ListStatus = "enqueued" | "working" | "pending" | "failed";

export type TabConfig = {
  id: TabId;
  label: string;
  listStatus: ListStatus | null;
};

export const TABS: TabConfig[] = [
  { id: "overview", label: "Overview", listStatus: null },
  { id: "enqueued", label: "Enqueued", listStatus: "enqueued" },
  { id: "working", label: "Working", listStatus: "working" },
  { id: "pending", label: "Pending", listStatus: "pending" },
  { id: "failed", label: "Failed", listStatus: "failed" }
];

export type CountMap = Record<string, number>;

export type OverviewCounts = {
  enqueued: number;
  working: number;
  pending: number;
  failed: number;
};

export type OverviewPayload = {
  counts: OverviewCounts;
  by_queue: CountMap;
  by_job_class: CountMap;
  by_job_class_and_status: {
    working: CountMap;
    pending: CountMap;
    failed: CountMap;
  };
};

export type Pagination = {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
};

export type DelayedJobRecord = {
  id: number;
  job_class: string;
  queue: string | null;
  priority: number;
  attempts: number;
  run_at: string | null;
  locked_at: string | null;
  locked_by: string | null;
  failed_at: string | null;
  job_arguments: unknown;
  last_error: string | null;
  created_at: string | null;
};

export type ListPayload = {
  records: DelayedJobRecord[];
  pagination: Pagination;
};

export type QueueParams = {
  queues?: string;
};

export type ListParams = {
  status: ListStatus;
  page: number;
  per_page: number;
  queues?: string;
  job_class?: string;
};

export type DelayedJobsState = {
  activeTab: TabId;
  queuesInput: string;
  jobClassInput: string;
  appliedQueues: string;
  appliedJobClass: string;
  page: number;
  overview: OverviewPayload;
  records: DelayedJobRecord[];
  pagination: Pagination;
  isLoading: boolean;
  actionPending: boolean;
  error: string | null;
  selectedJob: DelayedJobRecord | null;
};

export type RootState = {
  delayedJobs: DelayedJobsState;
};

export const EMPTY_OVERVIEW: OverviewPayload = {
  counts: { enqueued: 0, working: 0, pending: 0, failed: 0 },
  by_queue: {},
  by_job_class: {},
  by_job_class_and_status: { working: {}, pending: {}, failed: {} }
};

export const EMPTY_PAGINATION: Pagination = {
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  total_count: 0,
  total_pages: 1
};

export function buildListParams({
  status,
  page,
  queues,
  jobClass
}: {
  status: ListStatus;
  page: number;
  queues: string;
  jobClass: string;
}): ListParams {
  const params: ListParams = {
    status,
    page,
    per_page: DEFAULT_PER_PAGE
  };
  if (queues) params.queues = queues;
  if (jobClass) params.job_class = jobClass;
  return params;
}

export function buildQueueParams(queues: string): QueueParams {
  return queues ? { queues } : {};
}
